import { SEO } from "@/components/SEO";
import { PageSchema } from "@/components/seo/PageSchema";
import { LastUpdated } from "@/components/seo/LastUpdated";
import { TopCTA } from "@/components/cta/TopCTA";
import { MidPageCTA } from "@/components/cta/MidPageCTA";
import { BottomCTA } from "@/components/cta/BottomCTA";
import { SymptomChecker } from "@/components/content/SymptomChecker";
import { FixSteps } from "@/components/content/FixSteps";
import { DIYvsDoneForYou } from "@/components/content/DIYvsDoneForYou";
import { FAQSection } from "@/components/content/FAQSection";
import { InternalLinks } from "@/components/content/InternalLinks";
import { LeadFormSection } from "@/components/LeadFormSection";

const HighTrafficNoCalls = () => {
  const symptoms = [
    "Google Analytics shows 500+ monthly visitors but minimal leads",
    "Website conversion rate is under 1%",
    "Visitors spend time on site but don't take action",
    "You rank well for keywords but the phone doesn't ring",
    "Blog traffic is high but service page visits are low",
    "Bounce rate is normal but form submissions are near zero"
  ];

  const rootCauses = [
    { cause: "Traffic from wrong keywords (informational, not commercial)", impact: "Visitors are researching, not buying" },
    { cause: "No clear path from content to conversion", impact: "Visitors read and leave without next steps" },
    { cause: "Weak or missing calls-to-action", impact: "Nothing compels visitors to contact you" },
    { cause: "Phone number not prominent or clickable", impact: "Ready-to-call visitors can't find contact info easily" },
    { cause: "Quote form buried below the fold", impact: "Visitors don't scroll far enough to see it" },
    { cause: "No trust signals visible", impact: "Visitors don't feel confident reaching out" },
    { cause: "Traffic from outside your service area", impact: "Visitors can't use your services" },
    { cause: "Content doesn't address pain points", impact: "Visitors don't see you as the solution" }
  ];

  const fixSteps = [
    { step: "Audit your traffic sources and keywords", details: "In Google Analytics and Search Console, check if traffic is from commercial or informational keywords" },
    { step: "Add CTAs to every content piece", details: "Every blog post and page needs a clear next step pointing to services or contact" },
    { step: "Make your phone number sticky on mobile", details: "Add a floating call button that's always visible on mobile screens" },
    { step: "Place a quote form above the fold", details: "Visitors should see how to contact you without scrolling" },
    { step: "Add social proof throughout the site", details: "Display reviews, certifications, and customer counts on every page" },
    { step: "Filter traffic by location", details: "Add service area info prominently so out-of-area visitors know immediately" },
    { step: "Create bridge content between blog and services", details: "Link informational content to related service pages with compelling CTAs" }
  ];

  const faqs = [
    { question: "Why is my website getting traffic but no leads?", answer: "Usually the traffic is from informational keywords (people researching) rather than commercial keywords (people ready to buy). Or your site lacks clear conversion elements like prominent CTAs, visible phone numbers, and easy quote forms." },
    { question: "What's a good conversion rate for lawn care websites?", answer: "3-5% is typical for well-optimized lawn care websites. Under 1% indicates serious conversion problems. Above 5% suggests your traffic is highly targeted or your conversion elements are exceptional." },
    { question: "How do I know if my traffic is from the right keywords?", answer: "Check Google Search Console for your actual search queries. 'How to aerate lawn' is informational (low conversion intent). 'Lawn aeration service near me' is commercial (high conversion intent). You want more of the latter." },
    { question: "Should I care about blog traffic if it doesn't convert?", answer: "Blog traffic has value if it's strategic. Informational content can convert when paired with strong CTAs to related services. 'How often to mow grass' should link to your lawn mowing service with a compelling offer." },
    { question: "Where should I put my phone number?", answer: "Header (desktop), sticky button (mobile), within content, near every CTA, and in the footer. Make it clickable everywhere. The easier you make it to call, the more calls you'll receive." },
    { question: "What's the most effective CTA for lawn care websites?", answer: "'Get a Free Quote' or 'Schedule Your Free Estimate' work well. Include urgency when appropriate: 'Book Now—Spring Schedule Filling Fast.' Avoid generic 'Contact Us' which doesn't motivate action." },
    { question: "How do I filter out traffic from outside my service area?", answer: "Prominently display your service areas on every page. Add location-specific content. This doesn't reduce total traffic but helps visitors self-select, improving conversion quality." },
    { question: "Do trust signals really affect conversion?", answer: "Significantly. Websites with visible reviews, certifications, and customer counts convert 2-3x better than those without. People need reassurance before sharing contact info with a stranger." },
    { question: "Should I reduce traffic to improve conversion rate?", answer: "No—improve conversion elements instead. High traffic is valuable; you just need better capture mechanisms. Add CTAs, improve quote forms, and make contacting you effortless." },
    { question: "How quickly can I improve my website's conversion rate?", answer: "Basic fixes (adding CTAs, making phone clickable) can show results within days. Comprehensive optimization typically shows measurable improvement within 30 days of implementation." }
  ];

  const links = [
    { to: "/websites/", label: "Website Subscription Pricing" },
    { to: "/lawn-care-website-design/", label: "Lawn Care Website Design" },
    { to: "/website-fixes/lawn-care-website-not-getting-leads/", label: "Website Not Getting Leads" },
    { to: "/lawn-care-website-seo/", label: "Lawn Care Website SEO" }
  ];

  return (
    <>
      <SEO
        title="Website Getting Traffic But No Calls? Here's Why | Leaf Rush Marketing"
        description="High website traffic but minimal phone calls? Learn why visitors aren't converting and get actionable fixes to turn traffic into leads."
        url="https://www.leafrushmarketing.com/website-fixes/high-traffic-no-calls/"
      />
      <PageSchema
        url="https://www.leafrushmarketing.com/website-fixes/high-traffic-no-calls/"
        title="Website Getting Traffic But No Calls? Here's Why"
        description="High website traffic but minimal phone calls? Learn why visitors aren't converting and get fixes."
        faqs={faqs}
      />
      <LastUpdated />

      <article className="py-12">
        <div className="container mx-auto px-4 max-w-4xl">
          <header className="mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              Website Getting Traffic But No Calls? Here's Why
            </h1>
            <p className="text-xl text-muted-foreground">
              Hundreds of visitors, barely any leads. The traffic is there—the conversion isn't. Let's fix that.
            </p>
          </header>

          <TopCTA />

          <SymptomChecker symptoms={symptoms} title="Signs You Have a Conversion Problem, Not a Traffic Problem" />

          <section className="my-12">
            <h2 className="text-3xl font-bold text-foreground mb-6">Root Causes: Why Traffic Doesn't Convert</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {rootCauses.map((item, index) => (
                <div key={index} className="p-4 bg-muted rounded-lg">
                  <h3 className="font-semibold text-foreground">{item.cause}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{item.impact}</p>
                </div>
              ))}
            </div>
          </section>

          <MidPageCTA />

          <FixSteps steps={fixSteps} title="7 Steps to Convert Traffic Into Calls" />

          <section className="my-12">
            <h2 className="text-3xl font-bold text-foreground mb-6">The Traffic Trap: Vanity Metrics vs. Real Results</h2>
            <div className="prose prose-lg max-w-none text-foreground">
              <p>
                Website traffic feels like success, but it's meaningless without conversion. A site with 100 monthly visitors converting at 5% beats a site with 1,000 visitors converting at 0.1%. <strong>Leads pay the bills, not pageviews.</strong>
              </p>
              <p>
                The fix isn't always more traffic—it's better conversion. Strategic CTAs, visible contact info, trust signals, and user-friendly forms can double or triple your lead generation without changing your traffic at all.
              </p>
              <p>
                At <a href="/websites/" className="text-primary hover:underline">Leaf Rush Marketing</a>, we build websites optimized for conversion, not just traffic. Every element is designed to move visitors toward action—because a beautiful website that doesn't generate leads is just an expensive billboard.
              </p>
            </div>
          </section>

          <DIYvsDoneForYou />

          <FAQSection faqs={faqs} />

          <InternalLinks links={links} title="Related Resources" />

          <BottomCTA />

          <LeadFormSection title="Get a Free Conversion Audit" />
        </div>
      </article>
    </>
  );
};

export default HighTrafficNoCalls;
